import { Injectable, Provider } from '@angular/core';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { AddressIdentifierDto, EError, ProductIdentifierDto } from '../../INTEGRA_Shop_Client';
import { ErrorCodeService } from '../error-code.service';
import { IComparisonService, IComparisonServiceToken } from './comparison-service.interface';

@Injectable({
  providedIn: 'root'
})
export class ComparisonService implements IComparisonService {

  /**
   * Compares two objects in detail.
   * @param a First object to compare.
   * @param b Second object to compare.
   * @returns true if strict recursive equal, otherwise false.
   */
  public equalsGenericDeepSlow(a: any, b: any): boolean {
    if (a === b) { return true; } // null or undefined and exactly the same
    if (!(a instanceof Object) || !(b instanceof Object)) { return false; } // if they are not strictly equal, they both need to be Objects
    if (a.constructor !== b.constructor) { return false; } // they must have the exact same prototype chain, the closest we can do is test there constructor.
    for (let i in a) {
      if (!Object.prototype.hasOwnProperty.call(a, i)) { continue; } // other properties were tested using a.constructor === b.constructor
      if (!Object.prototype.hasOwnProperty.call(b, i)) { return false; } // allows to compare a[ p ] and b[ p ] when set to undefined
      if (a[i] === b[i]) continue; // if they have the same strict value or identity then they are equal
      if (typeof (a[i]) !== "object") { return false; } // Numbers, Strings, Functions, Booleans must be strictly equal
      if (!this.equalsGenericDeepSlow(a[i], b[i])) { return false; } // Objects and Arrays must be tested recursively
    }

    for (let i in b) {
      if (!Object.prototype.hasOwnProperty.call(b, i) && !Object.prototype.hasOwnProperty.call(a, i)) { return false; } // allows a[ p ] to be set to undefined
    }
    return true;
  }

  /**
   * Compares two ProductIdentifierDto.
   * @param a First ProductIdentifierDto
   * @param b Second ProductIdentifierDto
   * @returns true if Guid OR Number are equal and not undefined / null, otherwise false.
   */
  public equalsProductIdentifierByNumberOrGuid(a: ProductIdentifierDto | undefined, b: ProductIdentifierDto | undefined): boolean {
    if (a == undefined || b == undefined) {
      return false;
    }

    let guidEqual: boolean = a.productGuid == b.productGuid;
    let guidUndefined: boolean = a.productGuid == undefined || b.productGuid == undefined;

    if (guidEqual && !guidUndefined) {
      return true;
    }

    let numberEqual: boolean = a.productNumber == b.productNumber;
    let numberUndefined: boolean = a.productNumber == undefined || b.productNumber == undefined;

    if (numberEqual && !numberUndefined) {
      return true;
    }
    return false;
  }

  /**
   * Compares two ProductIdentifierDto.
   * @param a First ProductIdentifierDto
   * @param b Second ProductIdentifierDto
   * @returns true if Guid AND Number are equal, otherwise false.
   */
  public equalsProductIdentifierByNumberAndGuid(a: ProductIdentifierDto | undefined, b: ProductIdentifierDto | undefined): boolean {
    if (a == undefined || b == undefined) { return false; }
    if (a.productGuid == b.productGuid && a.productNumber == b.productNumber) { return true; }
    return false;
  }

  /**
   * Compares two AddressIdentifierDto.
   * @param a First AddressIdentifierDto
   * @param b Second AddressIdentifierDto
   * @returns true if number OR type are equal and not undefined / null, otherwise false.
   */
  public equalsAddressIdentifierByNumberOrType(a: AddressIdentifierDto | undefined, b: AddressIdentifierDto | undefined): boolean {
    if (a == undefined || b == undefined) {
      return false;
    }

    let numberEqual: boolean = a.addressNumber == b.addressNumber;
    let numberUndefined: boolean = a.addressNumber == undefined || b.addressNumber == undefined;

    if (numberEqual && !numberUndefined) {
      return true;
    }

    let typeEqual: boolean = a.addressType == b.addressType;
    let typeUndefined: boolean = a.addressType == undefined || b.addressType == undefined;

    if (typeEqual && !typeUndefined) {
      return true;
    }

    return false;
  }

  /**
   * Compares two AddressIdentifierDto.
   * @param a First AddressIdentifierDto
   * @param b Second AddressIdentifierDto
   * @returns true if Number AND Type are equal, otherwise false.
   */
  public equalsAddressIdentifierByNumberAndType(a: AddressIdentifierDto | undefined, b: AddressIdentifierDto | undefined): boolean {
    if (a == undefined || b == undefined) { return false; }
    if (a.addressNumber == b.addressNumber && a.addressType == b.addressType) { return true; }
    return false;
  }

  /**
   * Compares two Dates by Day, Month and Year.
   * @param a First Date or undefined
   * @param b Second Date or undefined
   * @returns true if both are undefined or Day, Month and Year are equal, otherwise false.
   */
  public equalsDateByValues(a: Date | undefined, b: Date | undefined): boolean {
    if (a == undefined && b == undefined) {
      return true;
    }

    if (a == undefined || b == undefined) {
      return false;
    }

    if (a.getFullYear() != b.getFullYear()) {
      return false;
    }

    if (a.getMonth() != b.getMonth()) {
      return false;
    }

    if (a.getDate() != b.getDate()) {
      return false;
    }

    return true;
  }

  /**
   * Compares two Dates by Year, Month and Day.
   * @param a First Date or undefined
   * @param b Second Date or undefined
   * @returns true if "a" is "newer" by Year, Month and Day or equal, otherwise false.
   */
  public biggerEqualsDateByValues(a: Date | undefined, b: Date | undefined): boolean {
    if (a == undefined && b == undefined) {
      return true;
    }

    if (a == undefined || b == undefined) {
      return false;
    }

    if (a.getFullYear() > b.getFullYear()) {
      return true;
    }

    if (a.getFullYear() < b.getFullYear()) {
      return false;
    }

    if (a.getMonth() > b.getMonth()) {
      return true;
    }

    if (a.getMonth() < b.getMonth()) {
      return false;
    }

    if (a.getDate() > b.getDate()) {
      return true;
    }

    if (a.getDate() < b.getDate()) {
      return false;
    }

    return true;
  }

  /**
   * Compares two Dates.
   * @param a
   * @param b
   * @returns true if "a" is "newer" or same as "b" by day, otherwise false.
   */
  public biggerEqualsTimeInMilli(a: Date, b: Date): boolean {
    if (a.getTime() >= b.getTime()) {
      return true;
    }
    return false;
  }

  /**
   * Compares two nullable strings and provides an integer result for sorting.
   * @param a
   * @param b
   * @returns 0 if equal, <0 if a is undefined or smaller, >0 if b is undefined or smaller
   */
  public static greaterThanNullableString(a: string | undefined, b: string | undefined): number {
    if (a == undefined && b == undefined) {
      return 0;
    }
    if (a == undefined && b != undefined) {
      return -1;
    }
    if (a != undefined && b == undefined) {
      return 0;
    }
    if (a == undefined || b == undefined) {
      throw new BasicError("Unreachable state in comparison. biggerEqualsNullableString", ErrorCodeService.formatErrorCode(EError.F330050));
    }

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }

  /**
   *  Checks whether the given entry element is in the provided list present.
   * @param list the list of elements to search through.
   * @param entry the entry or element to look for.
   * @returns true if found, otherwise false.
   */
  public isEntryinList(list: any[], entry: any): boolean {
    for (let i = list.length; i--;) {
      if (list[i] === entry) {
        return true;
      }
    }
    return false;
  }

  /**
   * Compares two lists against each other by searching each entry within the other list.
   * @param list1 the first list to compare.
   * @param list2 the second list to compare.
   * @returns true if each entry could be found in the other list, otherwise false.
   */
  public areListsEqual(list1: any[] | undefined, list2: any[] | undefined): boolean {
    if ((list1 == undefined || list1 == null) && (list2 == undefined && list2 == null)) {
      return true;
    }
    if ((list1 != undefined && list1 != null) && (list2 == undefined || list2 == null)) {
      return false;
    }
    if ((list1 == undefined || list1 == null) && (list2 != undefined && list2 != null)) {
      return false;
    }

    if (list1 == undefined || list2 == undefined) {
      return false;
    }

    if (list1.length !== list2.length) {
      return false;
    }

    for (let i = list1?.length; i--;) {
      if (!this.isEntryinList(list2, list1[i])) {
        return false;
      }
    }
    // might be unnecessary to check in both directions.
    for (let i = list2.length; i--;) {
      if (!this.isEntryinList(list1, list2[i])) {
        return false;
      }
    }
    return true;
  }
}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ComparisonServiceProvider: Provider[] = [
  { provide: IComparisonServiceToken, useExisting: ComparisonService }
];
